import supabase from '../../supabase-client.js';

// Database service for Librería Lithay
export class DatabaseService {
  
  // Supabase client for direct operations
  static supabase = supabase;
  
  // Get all published books
  static async getPublishedBooks() {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select(`
          *,
          author:authorsbook(id_author, name_author)
        `)
        .eq('available_product', true)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching published books:', error);
        return [];
      }

      // Process data to include category names
      const processedData = await Promise.all(
        (data || []).map(async (book) => {
          let categoryNames = [];
          if (book.categories_product && book.categories_product.length > 0) {
            categoryNames = await this.getCategoryNamesByIds(book.categories_product);
          }
          return {
            ...book,
            categoryNames
          };
        })
      );

      return processedData;
    } catch (error) {
      console.error('Error in getPublishedBooks:', error);
      return [];
    }
  }

  // Get books by category
  static async getBooksByCategory(category) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .contains('categories_product', [category])
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching books by category:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBooksByCategory:', error);
      return [];
    }
  }

  // Search books by title, author, or category
  static async searchBooks(query) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .ilike('title_product', `%${query}%`)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error searching books:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in searchBooks:', error);
      return [];
    }
  }

  // Get book by ID
  static async getBookById(id) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('id_product', id)
        .single();

      if (error) {
        console.error('Error fetching book by ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getBookById:', error);
      return null;
    }
  }

  // Get all categories
  static async getCategories() {
    try {
      const { data, error } = await supabase
        .from('categoriesbook')
        .select('id_category, name_category')
        .order('name_category');

      if (error) {
        console.error('Error fetching categories:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getCategories:', error);
      return [];
    }
  }

  // Get books on sale
  static async getBooksOnSale() {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching books on sale:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBooksOnSale:', error);
      return [];
    }
  }

  // Get favorite books
  static async getFavoriteBooks() {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .eq('favorite_product', true)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching favorite books:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getFavoriteBooks:', error);
      return [];
    }
  }

  // Toggle favorite status with count management for any product type
  static async toggleFavorite(productId, isFavorite, productType = 'book') {
    try {
      // Determine table and fields based on product type
      let tableName, idField, favoriteCountField, favoriteField;
      switch (productType) {
        case 'bible':
          tableName = 'biblebook';
          idField = 'id_bible';
          favoriteCountField = 'bible_favorite_count_product';
          favoriteField = 'bible_favorite_product';
          break;
        case 'thing':
          tableName = 'thingproduct';
          idField = 'id_thing';
          favoriteCountField = 'thing_favorite_count_product';
          favoriteField = 'thing_favorite_product';
          break;
        default: // book
          tableName = 'productbook';
          idField = 'id_product';
          favoriteCountField = 'favorite_count_product';
          favoriteField = 'favorite_product';
      }

      // Get current favorite count
      const { data: currentProduct, error: fetchError } = await supabase
        .from(tableName)
        .select(favoriteCountField)
        .eq(idField, productId)
        .single();

      if (fetchError) {
        console.error(`Error fetching current ${productType}:`, fetchError);
        return false;
      }

      const currentCount = currentProduct[favoriteCountField] || 0;
      const newCount = isFavorite ? currentCount + 1 : Math.max(0, currentCount - 1);

      // Prepare update object
      const updateObj = {
        [favoriteField]: newCount > 0,
        [favoriteCountField]: newCount
      };

      const { error } = await supabase
        .from(tableName)
        .update(updateObj)
        .eq(idField, productId);

      if (error) {
        console.error(`Error toggling favorite for ${productType}:`, error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in toggleFavorite:', error);
      return false;
    }
  }

  // Get books by price range
  static async getBooksByPriceRange(minPrice, maxPrice) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .gte('price_original_product', minPrice)
        .lte('price_original_product', maxPrice)
        .order('price_original_product', { ascending: true });

      if (error) {
        console.error('Error fetching books by price range:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBooksByPriceRange:', error);
      return [];
    }
  }

  // Get books by language
  static async getBooksByLanguage(language) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .eq('language_product', language)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching books by language:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBooksByLanguage:', error);
      return [];
    }
  }

  // Get books by publisher
  static async getBooksByPublisher(publisher) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
        .eq('publisher_product', publisher)
        .order('id_product', { ascending: false });

      if (error) {
        console.error('Error fetching books by publisher:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getBooksByPublisher:', error);
      return [];
    }
  }

  // Get author by ID
  static async getAuthorById(authorId) {
    try {
      const { data, error } = await supabase
        .from('authorsbook')
        .select('*')
        .eq('id_author', authorId)
        .single();

      if (error) {
        console.error('Error fetching author by ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getAuthorById:', error);
      return null;
    }
  }

  // Get category names by IDs
  static async getCategoryNamesByIds(categoryIds) {
    try {
      if (!categoryIds || categoryIds.length === 0) return [];

      const { data, error } = await supabase
        .from('categoriesbook')
        .select('name_category')
        .in('id_category', categoryIds);

      if (error) {
        console.error('Error fetching category names:', error);
        return [];
      }

      return data?.map(cat => cat.name_category) || [];
    } catch (error) {
      console.error('Error in getCategoryNamesByIds:', error);
      return [];
    }
  }

  // Get book by ID with author and category names
  static async getBookByIdWithDetails(id) {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select(`
          *,
          authorsbook (
            name_author
          )
        `)
        .eq('id_product', id)
        .single();

      if (error) {
        console.error('Error fetching book with details:', error);
        return null;
      }

      // Get category names if categories exist
      let categoryNames = [];
      if (data?.categories_product && data.categories_product.length > 0) {
        categoryNames = await this.getCategoryNamesByIds(data.categories_product);
      }

      return {
        ...data,
        categoryNames,
        authorName: data?.authorsbook?.name_author || null
      };
    } catch (error) {
      console.error('Error in getBookByIdWithDetails:', error);
      return null;
    }
  }

  // Get all authors
  static async getAuthors() {
    try {
      const { data, error } = await supabase
        .from('authorsbook')
        .select('*')
        .order('name_author', { ascending: true });

      if (error) {
        console.error('Error fetching authors:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAuthors:', error);
      return [];
    }
  }

  // Get all languages
  static async getLanguages() {
    try {
      const { data, error } = await supabase
        .from('productbook')
        .select('language_product')
        .not('language_product', 'is', null);

      if (error) {
        console.error('Error fetching languages:', error);
        return [];
      }

      // Extract unique languages
      const uniqueLanguages = [...new Set(data?.map(item => item.language_product).filter(Boolean))];
      return uniqueLanguages.map((lang, index) => ({ 
        id_language: index + 1, 
        name: lang 
      }));
    } catch (error) {
      console.error('Error in getLanguages:', error);
      return [];
    }
  }

  // Sort books by different criteria
  static async sortBooks(sortBy, sortOrder = 'asc') {
    try {
      let query = supabase
        .from('productbook')
        .select('*')
        .eq('available_product', true)
;

      switch (sortBy) {
        case 'price-asc':
          query = query.order('price_discount_product', { ascending: true });
          break;
        case 'price-desc':
          query = query.order('price_discount_product', { ascending: false });
          break;
        case 'newest':
          query = query.order('created_at_product', { ascending: false });
          break;
        case 'rating':
          query = query.order('rating_product', { ascending: false });
          break;
        case 'title':
          query = query.order('title_product', { ascending: true });
          break;
        default:
          query = query.order('created_at_product', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error sorting books:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in sortBooks:', error);
      return [];
    }
  }
}

export default DatabaseService;

